<template>
  <div class="min-h-screen bg-white py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="leading-relaxed space-y-10">
        <section>
          <h2 class="text-2xl font-semibold text-primary mb-3">Introduction</h2>
          <p>
            This Privacy Policy explains how MEDIUM.am (“we”, “our”, or “us”)
            collects, uses, and protects information when you use the MEDIUM.am
            platform (the “Service”).
          </p>
          <p class="mt-2">
            MEDIUM.am is a digital space where artists and art lovers meet. This
            policy describes our commitment to your privacy as you interact with
            our community and services.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-semibold text-primary mb-3">
            Account & Authentication Data
          </h2>
          <p>
            To access specific features and manage your profile, users must
            create an account or authenticate on the platform. Users may
            authenticate using email-based sign-up or Google Sign-In.
          </p>
          <p class="mt-4 font-medium">
            Regardless of whether a user signs in using email and password,
            Google Sign-In, or Facebook Login, we collect the same basic account
            information required to create and manage the user account:
          </p>
          <ul class="list-disc list-inside mt-2 space-y-1 ml-4">
            <li>First name</li>
            <li>Last name</li>
            <li>Email address</li>
            <li>Profile avatar (if provided)</li>
            <li>Phone number (if provided)</li>
            <li>Bio (if provided)</li>
            <li>Birthday (if provided)</li>
          </ul>
          <p class="mt-4 italic">
            No additional user data is accessed beyond the information listed
            above. We do not access your Google or Facebook contacts, calendars,
            friends lists, files, or any other third-party services. Signing in
            via Google, Facebook, or email/password provides the same limited
            information required for your account.
          </p>

          <p class="mt-2">
            This information is used solely for account identification,
            authentication, customer support, and service access control.
          </p>
          <p class="mt-2">
            Authentication is handled using secure, HTTP-only cookies for
            session management. We do not use cookies for advertising or
            tracking.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-semibold text-primary mb-3">
            Connecting Artists & Art Lovers
          </h2>
          <p>
            MEDIUM.am acts as a connector between creators and enthusiasts. We
            facilitate these connections by displaying the professional
            information provided by artists to the community.
          </p>
          <p class="mt-2">
            <strong>Payments:</strong> At this time, MEDIUM.am does not process
            payments or act as a payment intermediary. Any transactions or
            agreements made between users are handled externally and are not
            subject to this Privacy Policy.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-semibold text-primary mb-3">
            Cookies & Local Storage
          </h2>
          <p>
            The MEDIUM.am platform uses cookies strictly for authentication and
            session management. Minimal local storage may be used to save
            non-sensitive user preferences such as interface settings or
            language selection.
          </p>
          <p class="mt-2">
            We do not use tracking cookies, advertising cookies, or third-party
            analytics cookies.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-semibold text-primary mb-3">
            Instagram Import & Data Handling
          </h2>

          <p>
            MEDIUM.am allows registered users to import images from their own
            Instagram image posts and use them to create artwork drafts. To use
            this feature, users must connect an Instagram account they own and
            authorize MEDIUM.am to access the information required for the
            integration.
          </p>

          <p class="mt-2">
            <strong>Connecting an Instagram account:</strong> When users click
            "Connect Instagram", they are asked to sign in to Instagram, if
            necessary, and authorize the requested access through Meta's
            authorization process. On desktop browsers, this process opens in a
            popup window; on mobile devices, users are redirected to Instagram.
            After successful authorization, users return to their MEDIUM.am
            dashboard, where they can see whether their Instagram account is
            connected.
          </p>

          <p class="mt-2">
            <strong>Account information and token storage:</strong> To maintain
            the connection, MEDIUM.am securely stores the connected Instagram
            account's username, access token, and associated token validation
            and expiration information in its database. The initial short-lived
            access token is exchanged for a long-lived token that is valid for
            approximately 60 days. When the token expires, the connection
            becomes invalid, and the user must reconnect their Instagram account
            to continue using the feature. Users can disconnect their Instagram
            account at any time. Disconnecting permanently deletes the stored
            Instagram connection information from MEDIUM.am.
          </p>

          <p class="mt-2">
            <strong>Fetching and browsing posts:</strong> After connecting their
            account, users can open the Instagram import dialog to fetch and
            browse their available image posts. MEDIUM.am currently retrieves
            and displays image posts only; videos and other unsupported media
            types are not imported through this feature. Users can load
            additional posts in batches of up to 25 when more posts are
            available.
          </p>

          <p class="mt-2">
            <strong>Temporary post data:</strong> Post information fetched for
            browsing in the import dialog is held temporarily in the
            application's browser memory. Browsing data is not saved to our
            database or collected in the background. When the dialog is closed,
            this temporary data is cleared. Fetching and browsing posts does
            not, by itself, create artwork records or save the browsed posts to
            MEDIUM.am.
          </p>

          <p class="mt-2">
            <strong>Information saved when a post is imported:</strong> When
            users explicitly import a post, MEDIUM.am creates an artwork draft
            in its database. The draft includes the selected post's image and
            caption, the source Instagram post URL, and the Instagram post ID.
            The post ID is used to identify previously imported posts and help
            prevent the same post from being imported again. The artwork record
            is associated with the user's MEDIUM.am account and is stored as a
            draft until the user completes and publishes it.
          </p>

          <p class="mt-2">
            <strong>Drafts and publication:</strong> Imported artwork drafts are
            not publicly available in the MEDIUM.am gallery until the user has
            completed and published them. Users can edit their drafts before
            publication. Once published, an artwork becomes publicly available
            according to MEDIUM.am's normal publication settings and policies.
          </p>

          <p class="mt-2">
            <strong>Removing imported posts and artworks:</strong> Users can
            remove a post from the Instagram import list without necessarily
            deleting its associated artwork. If the associated artwork is still
            a draft, removing the imported post deletes the draft, and its
            stored artwork image files, as well as the Instagram import record.
            If the artwork has already been published, removing the post from
            the import list deletes only the associated Instagram import record;
            the published artwork and its associated content remain available on
            MEDIUM.am. This allows the user to import the Instagram post again
            later.
          </p>

          <p class="mt-2">
            Users may also delete their artwork separately, including published
            artwork, through the MEDIUM.am platform. When an artwork is deleted,
            its associated Instagram import record is also deleted if it still
            exists. Deleting an artwork does not delete the original post from
            Instagram.
          </p>

          <p class="mt-2">
            <strong>Purpose and sharing:</strong> Instagram account information
            and imported post data are used to maintain the connection, retrieve
            image posts at the user's request, and create artwork drafts
            explicitly selected by the user. MEDIUM.am does not sell this
            information or use it for unrelated background collection. Access to
            Instagram data is subject to the permissions granted by the user and
            Meta's authorization controls.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-semibold text-primary mb-3">
            Data Sharing & Disclosure
          </h2>
          <p>
            We do not sell, rent, or trade personal data. Data may be disclosed
            only if required by law or to comply with valid legal requests. In
            such cases, disclosure is limited to the minimum information legally
            required.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-semibold text-primary mb-3">Your Rights</h2>
          <ul class="list-disc list-inside space-y-2">
            <li>
              You may request access to your account information at any time.
            </li>
            <li>
              You may request correction or deletion of your account and
              personal data.
            </li>
            <li>
              You can log out at any time to clear authentication cookies.
            </li>
            <li>You may contact us with any privacy-related questions.</li>
          </ul>
        </section>

        <section>
          <h2 class="text-2xl font-semibold text-primary mb-3">Contact Us</h2>
          <p>
            If you have any questions or concerns regarding this Privacy Policy,
            you may contact us at:
          </p>
          <p class="text-primary font-bold mt-2">contact@medium.am</p>
        </section>
      </div>

      <div class="text-center mt-16 text-sm">
        <p>
          By using MEDIUM.am, you acknowledge that you have read and understood
          this Privacy Policy.
        </p>
      </div>
    </div>
  </div>
</template>
